<template>
  <div>
    <label class="block text-sm font-medium leading-6 text-gray-900">{{ label }}</label>
    <div class="mt-2 space-y-3">
      <!-- Color Preview -->
      <div class="flex items-center gap-3">
        <div
          class="w-20 h-20 rounded-lg border-2 border-gray-300 shadow-sm"
          :style="{ background: modelValue || '#000000' }"
        />
        <div class="flex-1">
          <input
            v-model="hexInput"
            type="text"
            placeholder="#000000 or rgba(0,0,0,1)"
            class="block w-full rounded-md border-0 py-1.5 px-3
              text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300
              placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            @input="onHexInput"
          >
        </div>
      </div>

      <!-- Color Type Tabs -->
      <div v-if="!disableGradient" class="flex gap-2 border-b border-gray-200">
        <button
          type="button"
          :class="[
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            colorType === 'solid'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          ]"
          @click="colorType = 'solid'"
        >
          Solid
        </button>
        <button
          type="button"
          :class="[
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            colorType === 'gradient'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          ]"
          @click="colorType = 'gradient'"
        >
          Gradient
        </button>
      </div>

      <!-- Solid Color Picker -->
      <div v-if="colorType === 'solid'" class="space-y-3">
        <div class="flex items-center gap-3">
          <label class="text-sm text-gray-700 w-20">Color:</label>
          <input
            v-model="solidColor"
            type="color"
            class="h-10 w-20 rounded cursor-pointer border border-gray-300"
            @input="updateSolidColor"
          >
        </div>

        <div class="flex items-center gap-3">
          <label class="text-sm text-gray-700 w-20">Opacity:</label>
          <input
            v-model.number="opacity"
            type="range"
            min="0"
            max="100"
            class="flex-1"
            @input="updateSolidColor"
          >
          <span class="text-sm text-gray-600 w-12">{{ opacity }}%</span>
        </div>
      </div>

      <!-- Gradient Picker -->
      <div v-if="colorType === 'gradient'" class="space-y-3">
        <div class="flex items-center gap-3">
          <label class="text-sm text-gray-700 w-20">Direction:</label>
          <select
            v-model="gradientDirection"
            class="flex-1 rounded-md border-0 py-1.5 px-3
              text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300
              focus:ring-2 focus:ring-primary-600 sm:text-sm"
            @change="updateGradient"
          >
            <option value="to right">
              → Left to Right
            </option>
            <option value="to left">
              ← Right to Left
            </option>
            <option value="to bottom">
              ↓ Top to Bottom
            </option>
            <option value="to top">
              ↑ Bottom to Top
            </option>
            <option value="to bottom right">
              ↘ Diagonal
            </option>
          </select>
        </div>

        <div class="space-y-2">
          <div class="flex items-center gap-3">
            <label class="text-sm text-gray-700 w-20">Color 1:</label>
            <input
              v-model="gradientColor1"
              type="color"
              class="h-10 w-20 rounded cursor-pointer border border-gray-300"
              @input="updateGradient"
            >
            <input
              v-model.number="gradientOpacity1"
              type="range"
              min="0"
              max="100"
              class="flex-1"
              @input="updateGradient"
            >
            <span class="text-sm text-gray-600 w-12">{{ gradientOpacity1 }}%</span>
          </div>

          <div class="flex items-center gap-3">
            <label class="text-sm text-gray-700 w-20">Color 2:</label>
            <input
              v-model="gradientColor2"
              type="color"
              class="h-10 w-20 rounded cursor-pointer border border-gray-300"
              @input="updateGradient"
            >
            <input
              v-model.number="gradientOpacity2"
              type="range"
              min="0"
              max="100"
              class="flex-1"
              @input="updateGradient"
            >
            <span class="text-sm text-gray-600 w-12">{{ gradientOpacity2 }}%</span>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div v-show="errorMsg" class="text-xs text-red-500 mt-0.5">
        {{ errorMsg }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  label: {
    type: String,
    default: 'Color'
  },
  errorMsg: {
    type: String,
    default: ''
  },
  disableGradient: {
    type: Boolean,
    default: false
  }
});

const modelValue = defineModel({
  type: String,
  default: '#000000'
});

const colorType = ref('solid');
const hexInput = ref(modelValue.value || '#000000');

// Solid color state
const solidColor = ref('#000000');
const opacity = ref(100);

// Gradient state
const gradientDirection = ref('to right');
const gradientColor1 = ref('#000000');
const gradientColor2 = ref('#ffffff');
const gradientOpacity1 = ref(100);
const gradientOpacity2 = ref(100);

// Force solid mode when gradient is disabled
watch(() => props.disableGradient, disabled => {
  if (disabled && colorType.value === 'gradient') {
    colorType.value = 'solid';
  }
}, { immediate: true });

// Watch colorType changes to update value when switching tabs
watch(colorType, newType => {
  if (newType === 'gradient' && !modelValue.value.startsWith('linear-gradient')) {
    updateGradient();
  } else if (newType === 'solid' && modelValue.value.startsWith('linear-gradient')) {
    updateSolidColor();
  }
});

// Initialize from modelValue
watch(() => modelValue.value, newVal => {
  if (!newVal) return;

  hexInput.value = newVal;

  if (newVal.startsWith('linear-gradient')) {
    colorType.value = 'gradient';
    parseGradient(newVal);
  } else {
    colorType.value = 'solid';
    parseSolidColor(newVal);
  }
}, { immediate: true });

function parseSolidColor(color) {
  if (color.startsWith('rgba')) {
    const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (match) {
      const r = parseInt(match[1]);
      const g = parseInt(match[2]);
      const b = parseInt(match[3]);
      const a = match[4] ? parseFloat(match[4]) : 1;

      solidColor.value = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
      opacity.value = Math.round(a * 100);
    }
  } else if (color.startsWith('#')) {
    solidColor.value = color.substring(0, 7);
    opacity.value = 100;
  }
}

function parseGradient(gradient) {
  // Simple parsing - can be improved
  const dirMatch = gradient.match(/linear-gradient\(([^,]+),/);
  if (dirMatch) {
    gradientDirection.value = dirMatch[1].trim();
  }
}

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function updateSolidColor() {
  const alpha = opacity.value / 100;
  const color = hexToRgba(solidColor.value, alpha);
  modelValue.value = color;
  hexInput.value = color;
}

function updateGradient() {
  const alpha1 = gradientOpacity1.value / 100;
  const alpha2 = gradientOpacity2.value / 100;
  const color1 = hexToRgba(gradientColor1.value, alpha1);
  const color2 = hexToRgba(gradientColor2.value, alpha2);

  const gradient = `linear-gradient(${gradientDirection.value}, ${color1}, ${color2})`;
  modelValue.value = gradient;
  hexInput.value = gradient;
}

function onHexInput(event) {
  const value = event.target.value;
  modelValue.value = value;

  if (value.startsWith('linear-gradient') && !props.disableGradient) {
    colorType.value = 'gradient';
    parseGradient(value);
  } else {
    colorType.value = 'solid';
    parseSolidColor(value);
  }
}
</script>
